import { defineStore } from 'pinia'

import { useDefaultStore } from '@/store/defaultStore'

import { getHttpRouterParameters, sendHttpRouterTestRequest } from '@/api/call-flows-routes'

import { parseParams } from '@/helpers/destinations/destination-request-helper'
import { getKeys, getKeysFromXML } from '@/helpers/app-helper'

import { CALL_FLOW_COMPONENTS } from '@/constants/call-tracking/call-flow/callFlowComponents'
import { PARAMETERS_TYPES } from '@/constants/parametersTypes'

export const useDetailCallFlowHttpRouterStore = defineStore('useDetailCallFlowHttpRouter', {
    state: () => ({
        items: [],
        testResponse: [],
        testResponseParams: [],
        loading: false,
    }),
    actions: {
        async goToLoadParamsThree(slug) {
            const defaultStore = useDefaultStore()

            const { success, payload, message } = await getHttpRouterParameters(slug)

            if (success) {
                this.items = parseParams(payload)
            } else {
                defaultStore.setErrorMessage({ message })
            }
        },

        extractCallerInputItemsFromBlocks(blocks) {
            return blocks
                .filter((block) => block.type_id === CALL_FLOW_COMPONENTS.callerInput.id && block.config?.parameter_name)
                .map((block) => ({
                    slug: block.slug,
                    key: block.config.parameter_name,
                    name: block.config.parameter_name,
                    callerInputItem: true,
                }))
        },

        clearCallerInputGroups(existingItem, name) {
            const updatedGroups = (existingItem.items || []).map((group) => {
                const isTargetGroup = group.label === PARAMETERS_TYPES.callFlow || group.label === name

                if (!isTargetGroup) {
                    return group
                }

                return {
                    ...group,
                    label: name,
                    slug: name,
                    items: [],
                }
            })

            return {
                ...existingItem,
                items: updatedGroups,
            }
        },

        createNewCallFlowParamsGroup(name, callerInputItems) {
            return {
                label: PARAMETERS_TYPES.callFlow,
                name: PARAMETERS_TYPES.callFlow,
                id: PARAMETERS_TYPES.callFlow,
                isOpen: false,
                items: [{
                    label: name,
                    isOpen: false,
                    items: callerInputItems,
                }],
            }
        },

        buildUpdatedGroups(existingGroups, name, callerInputItems) {
            let hasTargetGroup = false

            const updatedGroups = existingGroups.reduce((acc, group) => {
                if (group.label === name) {
                    hasTargetGroup = true
                    const uniqueItems = callerInputItems.filter(
                        (item, index, array) => array.findIndex((candidate) => candidate.slug === item.slug) === index,
                    )

                    acc.push({
                        ...group,
                        label: name,
                        items: uniqueItems,
                    })

                    return acc
                }

                const filteredItems = (group.items || []).filter(
                    (item) => !callerInputItems.some((candidate) => candidate.slug === item.slug),
                )

                if (filteredItems.length) {
                    acc.push({
                        ...group,
                        items: filteredItems,
                    })
                }

                return acc
            }, [])

            if (!hasTargetGroup) {
                updatedGroups.push({
                    label: name,
                    isOpen: false,
                    items: callerInputItems,
                })
            }

            return updatedGroups
        },

        updateCallerInputParams(blocks, name) {
            const callerInputItems = this.extractCallerInputItemsFromBlocks(blocks)
            const findIndex = this.items.findIndex((item) => item.label === PARAMETERS_TYPES.callFlow)

            if (!callerInputItems.length) {
                if (findIndex === -1) {
                    return
                }

                const updatedItem = this.clearCallerInputGroups(this.items[findIndex], name)
                this.items.splice(findIndex, 1, updatedItem)
                return
            }

            if (findIndex === -1) {
                this.items.push(this.createNewCallFlowParamsGroup(name, callerInputItems))
                return
            }

            const existingItem = this.items[findIndex]
            const updatedGroups = this.buildUpdatedGroups(existingItem.items || [], name, callerInputItems)

            const updatedItem = {
                ...existingItem,
                items: updatedGroups,
            }

            this.items.splice(findIndex, 1, updatedItem)
        },

        async sendTestRequest({ slug, block, formData }) {
            const defaultStore = useDefaultStore()

            this.loading = true

            const {
                success, payload, status, message,
            } = await sendHttpRouterTestRequest(slug, block, formData)

            if (success) {
                defaultStore.setSuccessMessage({ message })

                this.parseTestResponse(payload)
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.loading = false

            return {
                success, payload, status, message,
            }
        },

        parseTestResponse(payload) {
            if (payload.hasOwnProperty('xml') && payload.xml) {
                this.parseTestResponseXml(payload)
            } else {
                this.parseTestResponseJson(payload)
            }
        },

        parseTestResponseXml(payload) {
            this.testResponse = getKeysFromXML(payload.data.documentElement)
        },

        parseTestResponseJson(payload) {
            this.testResponse = getKeys(payload)
        },
    },
})
